/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import * as coreClient from "@azure/core-client";
import * as coreRestPipeline from "@azure/core-rest-pipeline";
import {
  PipelineRequest,
  PipelineResponse,
  SendRequest
} from "@azure/core-rest-pipeline";
import * as coreAuth from "@azure/core-auth";
import {
  UsageDetailsImpl,
  MarketplacesImpl,
  BudgetsImpl,
  TagsImpl,
  ChargesImpl,
  BalancesImpl,
  ReservationsSummariesImpl,
  ReservationsDetailsImpl,
  ReservationRecommendationsImpl,
  ReservationRecommendationDetailsImpl,
  ReservationTransactionsImpl,
  PriceSheetImpl,
  OperationsImpl,
  AggregatedCostImpl,
  EventsOperationsImpl,
  LotsOperationsImpl,
  CreditsImpl
} from "./operations";
import {
  UsageDetails,
  Marketplaces,
  Budgets,
  Tags,
  Charges,
  Balances,
  ReservationsSummaries,
  ReservationsDetails,
  ReservationRecommendations,
  ReservationRecommendationDetails,
  ReservationTransactions,
  PriceSheet,
  Operations,
  AggregatedCost,
  EventsOperations,
  LotsOperations,
  Credits
} from "./operationsInterfaces";
import { ConsumptionManagementClientOptionalParams } from "./models";

export class ConsumptionManagementClient extends coreClient.ServiceClient {
  $host: string;
  apiVersion: string;
  subscriptionId: string;

  /**
   * Initializes a new instance of the ConsumptionManagementClient class.
   * @param credentials Subscription credentials which uniquely identify client subscription.
   * @param subscriptionId Azure Subscription ID.
   * @param options The parameter options
   */
  constructor(
    credentials: coreAuth.TokenCredential,
    subscriptionId: string,
    options?: ConsumptionManagementClientOptionalParams
  ) {
    if (credentials === undefined) {
      throw new Error("'credentials' cannot be null");
    }
    if (subscriptionId === undefined) {
      throw new Error("'subscriptionId' cannot be null");
    }

    // Initializing default values for options
    if (!options) {
      options = {};
    }
    const defaults: ConsumptionManagementClientOptionalParams = {
      requestContentType: "application/json; charset=utf-8",
      credential: credentials
    };

    const packageDetails = `azsdk-js-arm-consumption/9.2.0`;
    const userAgentPrefix =
      options.userAgentOptions && options.userAgentOptions.userAgentPrefix
        ? `${options.userAgentOptions.userAgentPrefix} ${packageDetails}`
        : `${packageDetails}`;

    const optionsWithDefaults = {
      ...defaults,
      ...options,
      userAgentOptions: {
        userAgentPrefix
      },
      endpoint:
        options.endpoint ?? options.baseUri ?? "https://management.azure.com"
    };
    super(optionsWithDefaults);

    let bearerTokenAuthenticationPolicyFound: boolean = false;
    if (options?.pipeline && options.pipeline.getOrderedPolicies().length > 0) {
      const pipelinePolicies: coreRestPipeline.PipelinePolicy[] = options.pipeline.getOrderedPolicies();
      bearerTokenAuthenticationPolicyFound = pipelinePolicies.some(
        (pipelinePolicy) =>
          pipelinePolicy.name ===
          coreRestPipeline.bearerTokenAuthenticationPolicyName
      );
    }
    if (
      !options ||
      !options.pipeline ||
      options.pipeline.getOrderedPolicies().length == 0 ||
      !bearerTokenAuthenticationPolicyFound
    ) {
      this.pipeline.removePolicy({
        name: coreRestPipeline.bearerTokenAuthenticationPolicyName
      });
      this.pipeline.addPolicy(
        coreRestPipeline.bearerTokenAuthenticationPolicy({
          credential: credentials,
          scopes:
            optionsWithDefaults.credentialScopes ??
            `${optionsWithDefaults.endpoint}/.default`,
          challengeCallbacks: {
            authorizeRequestOnChallenge:
              coreClient.authorizeRequestOnClaimChallenge
          }
        })
      );
    }
    // Parameter assignments
    this.subscriptionId = subscriptionId;

    // Assigning values to Constant parameters
    this.$host = options.$host || "https://management.azure.com";
    this.apiVersion = options.apiVersion || "2021-10-01";
    this.usageDetails = new UsageDetailsImpl(this);
    this.marketplaces = new MarketplacesImpl(this);
    this.budgets = new BudgetsImpl(this);
    this.tags = new TagsImpl(this);
    this.charges = new ChargesImpl(this);
    this.balances = new BalancesImpl(this);
    this.reservationsSummaries = new ReservationsSummariesImpl(this);
    this.reservationsDetails = new ReservationsDetailsImpl(this);
    this.reservationRecommendations = new ReservationRecommendationsImpl(this);
    this.reservationRecommendationDetails = new ReservationRecommendationDetailsImpl(
      this
    );
    this.reservationTransactions = new ReservationTransactionsImpl(this);
    this.priceSheet = new PriceSheetImpl(this);
    this.operations = new OperationsImpl(this);
    this.aggregatedCost = new AggregatedCostImpl(this);
    this.eventsOperations = new EventsOperationsImpl(this);
    this.lotsOperations = new LotsOperationsImpl(this);
    this.credits = new CreditsImpl(this);
    this.addCustomApiVersionPolicy(options.apiVersion);
  }

  /** A function that adds a policy that sets the api-version (or equivalent) to reflect the library version. */
  private addCustomApiVersionPolicy(apiVersion?: string) {
    if (!apiVersion) {
      return;
    }
    const apiVersionPolicy = {
      name: "CustomApiVersionPolicy",
      async sendRequest(
        request: PipelineRequest,
        next: SendRequest
      ): Promise<PipelineResponse> {
        const param = request.url.split("?");
        if (param.length > 1) {
          const newParams = param[1].split("&").map((item) => {
            if (item.indexOf("api-version") > -1) {
              return "api-version=" + apiVersion;
            } else {
              return item;
            }
          });
          request.url = param[0] + "?" + newParams.join("&");
        }
        return next(request);
      }
    };
    this.pipeline.addPolicy(apiVersionPolicy);
  }

  usageDetails: UsageDetails;
  marketplaces: Marketplaces;
  budgets: Budgets;
  tags: Tags;
  charges: Charges;
  balances: Balances;
  reservationsSummaries: ReservationsSummaries;
  reservationsDetails: ReservationsDetails;
  reservationRecommendations: ReservationRecommendations;
  reservationRecommendationDetails: ReservationRecommendationDetails;
  reservationTransactions: ReservationTransactions;
  priceSheet: PriceSheet;
  operations: Operations;
  aggregatedCost: AggregatedCost;
  eventsOperations: EventsOperations;
  lotsOperations: LotsOperations;
  credits: Credits;
}
